import { createRef, forwardRef, HTMLAttributes, useMemo } from 'react'
import { Transition, TransitionGroup } from 'react-transition-group'
import gsap from 'gsap'
import { map } from 'lodash'
import { CheckIcon, HeartIcon } from 'lucide-react'
import Link from 'next/link'
import { useShallow } from 'zustand/shallow'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Image from '@/components/base/lazy-image'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useGridMotion } from '@/components/sections/motion/grid'
import { useCopyToClipboard, useSSRWishlist } from '@/hooks'
import { hasFinishing, isInWishlist, useWishlistStore } from '@/store/wishlist'
import { getColorCategoryLabel } from '@/utils/color'

import WishlistTools from './wishlist-tools'
import { cn } from '@/lib/utils'
import { Color } from '@/types'

export type ColorRowProps = Omit<HTMLAttributes<HTMLDivElement>, 'color'> & {
  color: Color
  isInWishlist: boolean
  onAddToWishlist: () => void
}

const ColorRow = forwardRef<HTMLDivElement, ColorRowProps>(
  ({ color, isInWishlist, onAddToWishlist, className, ...props }, ref) => {
    const { title, link, hex_color } = color
    const { copied, copyToClipboard } = useCopyToClipboard()

    return (
      <div ref={ref} className={cn('relative', className)} {...props}>
        <div
          className={cn(
            'grid grid-cols-[8rem_1fr_8.8rem] items-center gap-x-[2rem] md:grid-cols-[8rem_1fr_2fr_2fr_8.8rem]',
            'border-b border-dark/20 py-[1rem]'
          )}
          data-motion-card
        >
          <Link
            href={link}
            scroll={false}
            aria-label={`Apri il colore ${title}`}
            className={cn(
              'flex rounded-[0.5rem]',
              'after:absolute after:left-0 after:top-0 after:z-10 after:size-full',
              'focus-ring-dark'
            )}
          >
            <span
              className="flex aspect-square size-[8rem] rounded-[0.5rem]"
              style={{ backgroundColor: hex_color || '#dddddd' }}
            />
          </Link>

          <Typography variant="upper-small" className="font-bold uppercase">
            {title.toString()}
          </Typography>

          <div className="hidden items-center space-x-[1rem] md:flex">
            {color.finishing ? (
              <>
                <span className="relative flex aspect-square size-[6.4rem] overflow-hidden rounded-[0.5rem] border border-dashed border-dark/30">
                  <Image
                    src={color.finishing.acf?.image_thumb.url}
                    alt=""
                    fill
                    className="object-cover"
                  />
                </span>
                <Typography variant="upper-small" className="font-bold">
                  {color.finishing.name}
                </Typography>
              </>
            ) : null}
          </div>

          <Typography variant="upper-small" className="hidden font-bold uppercase md:flex">
            {getColorCategoryLabel(color)}
          </Typography>

          <span className="flex justify-end">
            <button
              type="button"
              aria-label={`Copia il colore ${hex_color}`}
              onClick={() => copyToClipboard(color.hex_color)}
              className={cn(
                'relative z-10 size-[4.4rem]',
                'flex items-center justify-center',
                'rounded-md hover:bg-light/0',
                'focus-ring-dark'
              )}
            >
              {copied ? (
                <CheckIcon size={16} />
              ) : (
                <SVGIcon symbol="copy" className="size-[1.6rem]" />
              )}
            </button>
            <button
              type="button"
              aria-label={
                isInWishlist ? 'Gestisci il colore nella wishlist' : 'Aggiungi alla wishlist'
              }
              aria-pressed={isInWishlist}
              className={cn(
                'relative z-10 size-[4.4rem]',
                'flex items-center justify-center',
                'rounded-md hover:bg-light/0',
                'focus-ring-dark'
              )}
              onClick={onAddToWishlist}
            >
              <HeartIcon
                size={16}
                strokeWidth="2"
                className={cn('transition-colors duration-300 ease-out-cubic', {
                  'fill-dark': isInWishlist
                })}
              />
            </button>
          </span>
        </div>
      </div>
    )
  }
)

export type ColorsRowsProps = HTMLAttributes<HTMLDivElement> & {
  collection: Color[]
}

const ColorsRows = ({ collection, className, ...props }: ColorsRowsProps) => {
  const wishlist = useSSRWishlist()
  const { motionRef } = useGridMotion()
  const [addToWishlist, removeFromWishlist, enableFromWishlist, disableFromWishlist] =
    useWishlistStore(
      useShallow((state) => [
        state.addToWishlist,
        state.removeFromWishlist,
        state.enableFromWishlist,
        state.disableFromWishlist
      ])
    )

  const list = useMemo(
    () => map(collection, (color) => ({ ...color, nodeRef: createRef<HTMLDivElement>() })),
    [collection]
  )

  return (
    <Section ref={motionRef} className={cn('min-h-[50vh]', className)} {...props}>
      <Container>
        <Grid className="gap-y-[4rem]">
          <TransitionGroup component={Grid} className="col-span-2 md:col-span-6 lg:col-span-12">
            {map(list, ({ id, nodeRef, ...color }) => {
              return (
                <Transition
                  key={`row-${id}`}
                  nodeRef={nodeRef}
                  timeout={500}
                  onExit={() => {
                    gsap.to(nodeRef.current, { opacity: 0, duration: 0.5, ease: 'power2.out' })
                  }}
                >
                  <ColorRow
                    ref={nodeRef}
                    color={{ ...color, id }}
                    isInWishlist={isInWishlist(id, wishlist)}
                    className="col-span-2 md:col-span-6 lg:col-span-12"
                    onAddToWishlist={() =>
                      isInWishlist(id, wishlist, true)
                        ? isInWishlist(id, wishlist)
                          ? hasFinishing(id, wishlist)
                            ? disableFromWishlist(id)
                            : removeFromWishlist(id)
                          : enableFromWishlist(id)
                        : addToWishlist({ ...color, id, finishing: null, enabled: true })
                    }
                  />
                </Transition>
              )
            })}
          </TransitionGroup>

          <div className={cn('col-span-2 md:col-span-6 lg:col-span-12', 'flex md:justify-end')}>
            <WishlistTools />
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default ColorsRows
